import { useEffect } from "react";
import type { DerivedAggregates, WorldState } from "../engine/types";
import { INDICATORS, trendFor } from "../engine/indicators";
import { useGame } from "../store/useGame";
import { HistoryGraph } from "./HistoryGraph";

interface Props {
  indicatorId: string;
  state: WorldState;
  aggregates: DerivedAggregates;
}

export function IndicatorDetailSheet({
  indicatorId,
  state,
  aggregates,
}: Props) {
  const close = useGame((s) => s.closeIndicator);
  const history = useGame((s) => s.indicatorHistory[indicatorId] ?? []);
  const decisions = useGame((s) => s.decisions);
  const played = useGame((s) => s.playedDecisions);
  const actOnIndicator = useGame((s) => s.actOnIndicator);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  const def = INDICATORS.find((i) => i.id === indicatorId);
  if (!def) return null;

  const value = def.extract(state, aggregates);
  const { direction, delta } = trendFor(history);
  const alertState =
    (def.alertAbove !== undefined && value > def.alertAbove) ||
    (def.alertBelow !== undefined && value < def.alertBelow);

  // « Agir » — proposé quand l'indicateur est préoccupant/critique et qu'un
  // levier de redressement réservé reste disponible.
  const verdict = def.verdict ? def.verdict(value) : null;
  const canAct =
    (verdict === "preoccupant" || verdict === "critique") &&
    decisions.some(
      (d) => d.remediation_for === indicatorId && !played.includes(d.id),
    );

  return (
    <>
      <div
        className="detail-backdrop"
        onClick={close}
        role="presentation"
        aria-hidden="true"
      />
      <aside
        className={`detail-sheet ${alertState ? "detail-sheet-alert" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Détail : ${def.label}`}
      >
        <div className="detail-handle" aria-hidden="true" />

        <header className="detail-head">
          <span className="detail-kicker">INDICATEUR</span>
          <button
            className="detail-close"
            onClick={close}
            aria-label="Fermer le détail"
            type="button"
          >
            ×
          </button>
        </header>

        <h2 className="detail-label">{def.label}</h2>

        <div className="detail-big-value">
          {def.format(value)}
          {def.unit && (
            <span className="detail-big-unit">{def.unit.trim()}</span>
          )}
        </div>

        {history.length >= 2 && (
          <div className={`detail-trend trend-${direction}`}>
            {direction === "up" ? "↑" : direction === "down" ? "↓" : "→"}{" "}
            {formatDelta(def.format, delta)} depuis T{history[0].turn}
          </div>
        )}

        {alertState && <div className="detail-alert">Zone critique</div>}

        {canAct && (
          <button
            type="button"
            className="detail-act"
            onClick={() => actOnIndicator(indicatorId)}
          >
            ⚡ Agir sur cet indicateur
          </button>
        )}

        <section className="detail-section">
          <h3 className="detail-sub">Historique</h3>
          <HistoryGraph history={history} def={def} />
        </section>

        <section className="detail-section">
          <h3 className="detail-sub">Ce que mesure cet indicateur</h3>
          <p className="detail-body">{def.description}</p>
        </section>

        {(def.alertAbove !== undefined || def.alertBelow !== undefined) && (
          <section className="detail-section">
            <h3 className="detail-sub">Seuils</h3>
            <p className="detail-body">
              {def.alertAbove !== undefined && (
                <>
                  Alerte au-dessus de{" "}
                  <strong>
                    {def.format(def.alertAbove)}
                    {def.unit}
                  </strong>
                  .{" "}
                </>
              )}
              {def.alertBelow !== undefined && (
                <>
                  Alerte en dessous de{" "}
                  <strong>
                    {def.format(def.alertBelow)}
                    {def.unit}
                  </strong>
                  .
                </>
              )}
            </p>
          </section>
        )}

        <p className="detail-footer">
          Le bandeau « Ce tour-ci » du cockpit liste les mouvements du dernier
          tour et leur contexte. L'attribution décision par décision viendra
          avec les révélations de mécanismes.
        </p>
      </aside>
    </>
  );
}

function formatDelta(format: (v: number) => string, delta: number): string {
  const s = format(Math.abs(delta));
  return delta >= 0 ? `+${s}` : `-${s}`;
}
